import React, { useEffect, useState } from "react";
import Api from "../api";
import {
    Breadcrumb,
    Button,
    DatePicker,
    Input,
    Layout,
    Modal,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    Collapse,
    Divider,
    theme
} from "antd";
import { ReloadOutlined } from '@ant-design/icons';
import moment from "moment";
import { useNavigate, useSearchParams } from "react-router-dom";

const { Text } = Typography;
const { Content } = Layout;
const { RangePicker } = DatePicker;
const { Panel } = Collapse;
const { useToken } = theme;

const AdminLogs = () => {
    const { token } = useToken();
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [meta, setMeta] = useState({});
    const [searchParams, setSearchParams] = useSearchParams();
    const [selectedLog, setSelectedLog] = useState(null);
    const [modalVisible, setModalVisible] = useState(false);
    const [autoRefresh, setAutoRefresh] = useState(false);
    const navigate = useNavigate();

    // Filter states
    const [adminId, setAdminId] = useState(searchParams.get('admin_id') || '');
    const [action, setAction] = useState(searchParams.get('action') || '');
    const [modelType, setModelType] = useState(searchParams.get('model_type') || '');
    const [dateRange, setDateRange] = useState([
        searchParams.get('from_date') ? moment(searchParams.get('from_date')) : null,
        searchParams.get('to_date') ? moment(searchParams.get('to_date')) : null
    ]);

    useEffect(() => {
        fetchLogs();
    }, [searchParams]);

    useEffect(() => {
        let interval;
        if (autoRefresh) {
            interval = setInterval(() => {
                fetchLogs();
            }, 30000); // 30 seconds
        }
        return () => clearInterval(interval);
    }, [autoRefresh, searchParams]);

    const fetchLogs = (page = 1) => {
        setLoading(true);
        const params = {
            page,
            admin_id: searchParams.get('admin_id') || undefined,
            action: searchParams.get('action') || undefined,
            model_type: searchParams.get('model_type') || undefined,
            from_date: searchParams.get('from_date') || undefined,
            to_date: searchParams.get('to_date') || undefined,
        };

        Api.get('/admin/admin-logs', params)
            .then(res => {
                setLogs(res.data.data);
                setMeta(res.data.meta);
                setLoading(false);
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const applyFilters = () => {
        const params = {};
        if (adminId) params.admin_id = adminId;
        if (action) params.action = action;
        if (modelType) params.model_type = modelType;
        if (dateRange && dateRange[0] && dateRange[1]) {
            params.from_date = dateRange[0].format('YYYY-MM-DD');
            params.to_date = dateRange[1].format('YYYY-MM-DD');
        }
        setSearchParams(params);
    };

    const clearFilters = () => {
        setAdminId('');
        setAction('');
        setModelType('');
        setDateRange([null, null]);
        setSearchParams({});
    };

    const openLogDetail = (log) => {
        setSelectedLog(log);
        setModalVisible(true);
    };

    const getActionColor = (action) => {
        switch (action) {
            case 'created':
                return 'green';
            case 'updated':
                return 'blue';
            case 'deleted':
                return 'red';
            default:
                return 'default';
        }
    };

    const getShortModelType = (modelType) => {
        if (!modelType) return '-';
        const parts = modelType.split('\\');
        return parts[parts.length - 1];
    };

    const getChangesCount = (changes) => {
        if (!changes || typeof changes !== 'object') return 0;
        return Object.keys(changes).length;
    };

    const renderChangeValue = (value) => {
        if (value === null) return <Text type="secondary">null</Text>;
        if (typeof value === 'boolean') return value ? 'true' : 'false';
        if (typeof value === 'object') return JSON.stringify(value, null, 2);
        return String(value);
    };

    const columns = [
        {
            title: 'ID',
            dataIndex: 'id',
            key: 'id',
            width: 70,
        },
        {
            title: 'Admin ID',
            dataIndex: 'admin_id',
            key: 'admin_id',
            width: 100,
        },
        {
            title: 'Action',
            dataIndex: 'action',
            key: 'action',
            width: 100,
            render: (action) => (
                <Tag color={getActionColor(action)}>{action}</Tag>
            ),
        },
        {
            title: 'Model Type',
            dataIndex: 'model_type',
            key: 'model_type',
            render: (modelType) => (
                <Text code>{getShortModelType(modelType)}</Text>
            ),
        },
        {
            title: 'Model ID',
            dataIndex: 'model_id',
            key: 'model_id',
            width: 100,
        },
        {
            title: 'Changes',
            dataIndex: 'changes',
            key: 'changes',
            width: 120,
            render: (changes) => {
                const count = getChangesCount(changes);
                return count > 0 ? `${count} field${count !== 1 ? 's' : ''} changed` : '-';
            },
        },
        {
            title: 'IP Address',
            dataIndex: 'ip_address',
            key: 'ip_address',
            width: 140,
            render: (ip) => <Text code>{ip}</Text>,
        },
        {
            title: 'Timestamp',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 160,
            render: (date) => moment(date).format('DD.MM.YYYY HH:mm:ss'),
        },
    ];

    return (
        <Content style={{ margin: '0 16px' }}>
            <Breadcrumb style={{ margin: '16px 0' }}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Admin Logs</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Row justify="space-between" align="middle" style={{ marginBottom: 20 }}>
                    <h1>Admin Logs ({meta.total || 0})</h1>
                    <Space>
                        <Button
                            type={autoRefresh ? 'primary' : 'default'}
                            onClick={() => setAutoRefresh(!autoRefresh)}
                        >
                            {autoRefresh ? 'Auto-refresh ON' : 'Auto-refresh OFF'}
                        </Button>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => fetchLogs(meta.current_page || 1)}
                        >
                            Refresh
                        </Button>
                    </Space>
                </Row>

                <div style={{ marginBottom: 20, padding: 16, background: token.colorBgElevated, borderRadius: 8, border: `1px solid ${token.colorBorder}` }}>
                    <Row gutter={[16, 16]}>
                        <Space wrap style={{ width: '100%' }}>
                            <Input
                                placeholder="Admin ID"
                                value={adminId}
                                onChange={(e) => setAdminId(e.target.value)}
                                style={{ width: 150 }}
                            />
                            <Select
                                placeholder="Action"
                                value={action || undefined}
                                onChange={setAction}
                                style={{ width: 150 }}
                                allowClear
                            >
                                <Select.Option value="created">Created</Select.Option>
                                <Select.Option value="updated">Updated</Select.Option>
                                <Select.Option value="deleted">Deleted</Select.Option>
                            </Select>
                            <Input
                                placeholder="Model Type"
                                value={modelType}
                                onChange={(e) => setModelType(e.target.value)}
                                style={{ width: 200 }}
                            />
                            <RangePicker
                                value={dateRange}
                                onChange={setDateRange}
                                format="YYYY-MM-DD"
                            />
                            <Button type="primary" onClick={applyFilters}>
                                Apply Filters
                            </Button>
                            <Button onClick={clearFilters}>
                                Clear Filters
                            </Button>
                        </Space>
                    </Row>
                </div>

                <Table
                    loading={loading}
                    columns={columns}
                    dataSource={logs}
                    rowKey="id"
                    size="small"
                    pagination={{
                        current: meta.current_page,
                        pageSize: meta.per_page,
                        total: meta.total,
                        showSizeChanger: false,
                    }}
                    onChange={(pagination) => fetchLogs(pagination.current)}
                    onRow={(record) => ({
                        onClick: () => openLogDetail(record),
                        style: { cursor: 'pointer' },
                    })}
                />
            </div>

            <Modal
                title="Admin Log Details"
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                width={800}
            >

                {selectedLog && (
                    <div>
                        <Space direction="vertical" style={{ width: '100%' }} size="large">
                            <div>
                                <Row gutter={[16, 8]}>
                                    <div style={{ width: '100%' }}>
                                        <Text strong>ID:</Text> <Text>{selectedLog.id}</Text>
                                        <Divider type="vertical" />
                                        <Text strong>Admin ID:</Text> <Text>{selectedLog.admin_id}</Text>
                                        <Divider type="vertical" />
                                        <Text strong>Action:</Text> <Tag color={getActionColor(selectedLog.action)}>{selectedLog.action}</Tag>
                                    </div>
                                    <div style={{ width: '100%', marginTop: 8 }}>
                                        <Text strong>Model:</Text> <Text code>{getShortModelType(selectedLog.model_type)}</Text>
                                        <Divider type="vertical" />
                                        <Text strong>Model ID:</Text> <Text>{selectedLog.model_id}</Text>
                                    </div>
                                    <div style={{ width: '100%', marginTop: 8 }}>
                                        <Text strong>IP Address:</Text> <Text code>{selectedLog.ip_address}</Text>
                                        <Divider type="vertical" />
                                        <Text strong>Timestamp:</Text> <Text>{moment(selectedLog.created_at).format('DD.MM.YYYY HH:mm:ss')}</Text>
                                    </div>
                                </Row>
                            </div>

                            <Divider />

                            {selectedLog.changes && Object.keys(selectedLog.changes).length > 0 && (
                                <div>
                                    <Text strong style={{ fontSize: 16 }}>Changes</Text>
                                    <div style={{ marginTop: 12 }}>
                                        {Object.entries(selectedLog.changes).map(([field, change]) => (
                                            <div
                                                key={field}
                                                style={{
                                                    marginBottom: 16,
                                                    padding: 12,
                                                    background: token.colorBgElevated,
                                                    border: `1px solid ${token.colorBorder}`,
                                                    borderRadius: 6,
                                                }}
                                            >
                                                <Text strong style={{ display: 'block', marginBottom: 8 }}>
                                                    {field}
                                                </Text>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    <div style={{ flex: 1 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>Old:</Text>
                                                        <div
                                                            style={{
                                                                padding: 8,
                                                                background: token.colorErrorBg,
                                                                border: `1px solid ${token.colorErrorBorder}`,
                                                                borderRadius: 4,
                                                                marginTop: 4,
                                                                fontFamily: 'monospace',
                                                                fontSize: 12,
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            <Text delete style={{ color: token.colorError }}>
                                                                {renderChangeValue(change.old)}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                    <Text strong style={{ fontSize: 18 }}>→</Text>
                                                    <div style={{ flex: 1 }}>
                                                        <Text type="secondary" style={{ fontSize: 12 }}>New:</Text>
                                                        <div
                                                            style={{
                                                                padding: 8,
                                                                background: token.colorSuccessBg,
                                                                border: `1px solid ${token.colorSuccessBorder}`,
                                                                borderRadius: 4,
                                                                marginTop: 4,
                                                                fontFamily: 'monospace',
                                                                fontSize: 12,
                                                                whiteSpace: 'pre-wrap',
                                                                wordBreak: 'break-word',
                                                            }}
                                                        >
                                                            <Text style={{ color: token.colorSuccess }}>
                                                                {renderChangeValue(change.new)}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {(!selectedLog.changes || Object.keys(selectedLog.changes).length === 0) && (
                                <Text type="secondary">No changes recorded</Text>
                            )}

                            <Collapse ghost>
                                <Panel header="Full Old Values (JSON)" key="1">
                                    <pre
                                        style={{
                                            background: token.colorBgElevated,
                                            border: `1px solid ${token.colorBorder}`,
                                            color: token.colorText,
                                            padding: 12,
                                            borderRadius: 6,
                                            overflow: 'auto',
                                            maxHeight: 300,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                        }}
                                    >
                                        {JSON.stringify(selectedLog.old_values, null, 2)}
                                    </pre>
                                </Panel>
                                <Panel header="Full New Values (JSON)" key="2">
                                    <pre
                                        style={{
                                            background: token.colorBgElevated,
                                            border: `1px solid ${token.colorBorder}`,
                                            color: token.colorText,
                                            padding: 12,
                                            borderRadius: 6,
                                            overflow: 'auto',
                                            maxHeight: 300,
                                            fontFamily: 'monospace',
                                            fontSize: 12,
                                        }}
                                    >
                                        {JSON.stringify(selectedLog.new_values, null, 2)}
                                    </pre>
                                </Panel>
                            </Collapse>

                            <div style={{ marginTop: 16 }}>
                                <Text strong>User Agent:</Text>
                                <div
                                    style={{
                                        marginTop: 8,
                                        padding: 8,
                                        background: token.colorBgElevated,
                                        border: `1px solid ${token.colorBorder}`,
                                        borderRadius: 4,
                                        fontFamily: 'monospace',
                                        fontSize: 11,
                                        wordBreak: 'break-all',
                                    }}
                                >
                                    {selectedLog.user_agent || 'N/A'}
                                </div>
                            </div>
                        </Space>
                    </div>
                )}
            </Modal>
        </Content>
    );
};

export default AdminLogs;
